<script setup lang="ts">
import rvapi from "@/rvapi";
import { ref } from "vue";
import { v4 as uuid } from "uuid";
import { chunk } from "@/utils";
const output = ref([] as string[]);
const search = async (q: string[], type: string, l: number) => {
  const qs = q
    .map((qq) => encodeURIComponent(qq.trim()))
    .filter((qq) => qq.length > 0);
  const searches = chunk(qs, 50);
  const results = [];
  for (const ids of searches) {
    const result = await rvapi.searchLegacy(type, ids, l);
    for (const r of result) {
      results.push(r);
    }
  }
  return results;
};

const loading = ref(false);
const email = ref("");
const go = async () => {
  loading.value = true;
  timeline.value = [];
  output.value = ["looking for legacy applications"];
  const eapps = await search([email.value], "application/email", 100);
  if (!eapps.length) {
    output.value.push("no apps found");
    loading.value = false;
    return;
  }
  const udids = eapps.map((a: any) => a.UDID);
  const apps = await search(udids, "application/device", 100);
  const appIds = apps.map((a: any) => a.AppID);
  output.value.push(`found ${apps.length} apps, looking for devices`);
  addToTimeline(apps, tlFromApp);

  const devices = await search(udids, "device/id", 100);
  addToTimeline(devices, tlFromDevice);

  output.value.push(`looking for postcards`);
  const cards = await search(appIds, "postcard/application", 100);
  addToTimeline(cards, tlFromPostcard);

  output.value.push(`looking for transactions`);
  const transactions = await search(appIds, "transaction/application", 100);
  addToTimeline(transactions, tlFromTransaction);

  output.value.push(`looking for event log entries`);
  const eventlog = await search(appIds, "eventlog/application", 100);
  addToTimeline(eventlog, tlFromEventlog);

  output.value.push(`looking for send log entries`);
  const sendlog = await search(appIds, "sendlog/application", 100);
  addToTimeline(sendlog, tlFromSendlog);

  loading.value = false;
  output.value = [];
};
const addToTimeline = (source: any, mapper: any) => {
  source.map(mapper).forEach((i: TimelineItem) => timeline.value.push(i));
  sortTimeline();
};

const tlFromApp = (app: any): TimelineItem => {
  return {
    type: "application",
    title: "Application",
    date: new Date(app.RegistrationDate),
    data: {
      kundnr: app.CustomerNo,
      info: app.DeviceDescription,
    },
    id: app.AppID.substring(0, 8),
    key: uuid(),
  };
};
const tlFromPostcard = (card: any): TimelineItem => {
  return {
    type: "card",
    title: "Postcard",
    date: new Date(card.CreatedDate),
    data: {
      ordernumber: card.OrderNumber,
      paidcards: card.PaidCards,
      freecards: card.FreeCards,
      transactionid: card.TransactionID,
      sendlogid: card.SendLogID,
    },
    id: "card",
    key: uuid(),
  };
};
const tlFromTransaction = (transaction: any): TimelineItem => {
  return {
    type: "transaction",
    title: "Transaction",
    date: new Date(transaction.CreatedAt),
    data: {
      app: transaction.AppID.substring(0, 8),
      customer: transaction.CustomerRefNo,
      amount: transaction.RequestedAmount,
      currency: transaction.CurrencyIsoCode,
      credits: transaction.RequestedCredits,
      payment: transaction.PaymentMethod,
      status: transaction.StatusCode,
    },
    id: transaction.TransactionID,
    key: uuid(),
  };
};
const tlFromEventlog = (eventlog: any): TimelineItem => {
  return {
    type: "eventlog",
    title: "Admin Log",
    date: new Date(eventlog.CreatedDate),
    data: {
      app: eventlog.AppID.substring(0, 8),
      message: `${eventlog.Message} / ${eventlog.CreatedBy}`,
    },
    colspan: 5,
    id: eventlog.RowID,
    key: uuid(),
  };
};
const tlFromSendlog = (sendlog: any): TimelineItem => {
  return {
    type: "sendlog",
    title: "SendLog",
    date: new Date(sendlog.ConsumeDate),
    data: {
      app: sendlog.AppID.substring(0, 8),
      transaction: sendlog.TransactionID,
      freecards: sendlog.FreeCards,
      paidcards: sendlog.PaidCards,
      paidamount: sendlog.PaidAmount,
      currency: sendlog.CurrencyIsoCode,
    },
    id: sendlog.RowID,
    key: uuid(),
  };
};
const tlFromDevice = (device: any): TimelineItem => {
  return {
    type: "device",
    title: "Device",
    date: new Date(device.RegistrationDate),
    data: { balance: device.Balance },
    id: device.UDID.substring(0, 8),
    key: uuid(),
  };
};
type TimelineItem = {
  title: string;
  data: Record<string, string>;
  date: Date;
  key: string;
  id: string;
  type: string;
  colspan?: number;
  balance?: number;
};
const getFromTo = (
  idMap: Record<string, string>,
  matches: string[],
  selfId: string,
): { from: string; to: string } => {
  if (matches[1] === "till") {
    return { from: selfId, to: idMap[matches[2]] };
  }
  return { to: selfId, from: idMap[matches[2]] };
};
const sortTimeline = () => {
  timeline.value.sort((a, b) => {
    return a.date.getTime() - b.date.getTime();
  });
  const balances: Record<string, number | undefined> = { test: 1 };
  const idLookup: Record<string, string> = {};
  timeline.value.forEach((item: TimelineItem) => {
    try {
      if (item.type === "application") {
        if (item?.data?.kundnr) {
          idLookup[item.data.kundnr] = item.id;
        }
      } else if (item.type === "device") {
        balances[item.id] = parseInt(item?.data?.balance, 10) ?? 0;
        item.balance = balances[item.id];
      } else if (item.type === "eventlog") {
        const transaction =
          /^Förköpta kort flyttade (från|till) ([a-zA-Z0-9]+)/;
        const match = item.data.message.match(transaction);
        if (match && match.length > 2) {
          const targets = getFromTo(idLookup, match, item.data.app);
          if (targets.from && balances[targets.from]) {
            const movedBalanceAmount = balances[targets.from] ?? 0;
            balances[targets.from] = 0;
            balances[targets.to] =
              (balances[targets.to] ?? 0) + movedBalanceAmount;
          }
        } else {
          const balanceDiff = /^(-?\d+) (Promotionkort|gratiskort)/;
          const match = item.data.message.match(balanceDiff);
          if (match && match.length > 1) {
            balances[item.data.app] =
              (balances[item.data.app] ?? 0) + parseInt(match[1], 10);
          }
        }
        item.balance = balances[item.data.app];
      } else if (item.type == "sendlog") {
        balances[item.data.app] =
          (balances[item.data.app] ?? 0) - parseInt(item.data.freecards, 10);
        item.balance = balances[item.data.app];
      }
    } catch {
      console.log("error in balance calculation");
    }
  });
};
const timeline = ref([] as TimelineItem[]);
</script>

<template>
  <h1>Timeline</h1>
  <p>
    <pn-input v-model="email" label="Email" />
  </p>
  <pn-button v-show="!!email" :loading="loading" @click="go">Go</pn-button>
  <pre v-html="output.join('\n')"></pre>

  <section v-show="!timeline.length">
    <h2>Here be dragons</h2>
    <p>
      The information presented here is interesting, but usually not complete.
      It is also important to keep in mind that the data model has evolved over
      the years, leading to missing information in some areas.
    </p>
    <p>In either case, I'll try to summarize the important parts here.</p>
    <h3>Device</h3>
    <p>
      This represents a customer mobile phone device. Usually consistent across
      app upgrades, but when the user changes phones, this object is replaced as
      well. It is primarily identified through a property called <b>UdID</b>, a
      consistent (usually) unique identifier for that phone. Sometimes
      ios-devices used capital letters in the id, which can cause issues when
      doing case-sensitive searches.
    </p>
    <p>
      This object has the <b>Legacy Balance</b> stored on it, which is the main
      headache we live with nowadays. Other than that, it also holds a date for
      when we last saw a customer use the device.
    </p>
    <p>
      There is one special unique Device-object with the id "web" which was used
      to identify all customers using the website as a collective. We can now
      determine that this was a bad idea, and that it complicated the domain
      model severely. If you see a udId just saying "web" please know that can
      refer to any of thousands of customers and purchases.
    </p>
    <h3>Application</h3>
    <p>
      Application is closely connected to Device, where one Device must have at
      least one (but can hold multiple) applications. I assume this is related
      to app upgrades and similar, but it looks inconsistent.
    </p>
    <p>
      Application is identified through the <b>AppID</b> property, which is
      usually similar to the udid, but sometimes a numerical suffix. Just like
      with Device, there is a special Application object with the id "web" that
      we should avoid.
    </p>
    <p>
      Application also has a <b>CustomerNumber</b> and <b>CustomerEmail</b>.
      These are the most common ways we can identify legacy customers. There is
      also some device and app version data, timestamps for when the customer
      last used the application, and also a flag mentioning a newsletter
      subscription. We do not use the newsletter flag on this object anymore,
      instead we keep track of newsletter subscriptions directly in sendgrid.
    </p>
    <h3>Postcard</h3>
    <p>
      This is one of the central objects in the entire system. It represents one
      postcard - but can be sent to multiple receivers. This means that the
      purchase can be for more than 1 card per postcard-object.
    </p>
    <p>
      It is usually connected to an Application and a Sendlog-entry. Sometimes
      also to a Transaction. It is also usually not really very helpful, because
      its use has shifted over the years, and it does not always hold the
      correct data in its fields. Specifically....
    </p>
    <p>
      The <b>PaidCards</b> and <b>FreeCards</b> fields have been used at times
      to track the number of cards paid for vs given as promotions or
      prepurchased - but it is not 100% reliable. I'd suggest using the
      properties found on SendLog instead.
    </p>
    <h3>Transaction</h3>
    <p>
      This object represents a transfer of money. Usually this is a card,
      purchased and sent, but sometimes it is prepurchases, and there are traces
      of other implementations in the code as well.
    </p>
    <p>
      There are a bunch of properties on this object with names like
      <b>RequestedAmount</b> and <b>TransactionId</b>, and they probably mean
      what you think they mean. Amount is usually expressed in cents. (So if
      amount is 10000 - that usually meant that 100.00SEK was paid)
    </p>
    <h3>Sendlog</h3>
    <p>
      As far as i can tell, this log held one entry per actually posted
      postcard. The <b>PaidCards</b> and <b>FreeCards</b> properties here were
      very dependable when keeping track of used legacy balance and sent cards
      up until sometime mid 2021, after which it usually marks everything as 0.
    </p>
    <h3>Adminlog</h3>
    <p>
      The most important type of object, in my opinion. Connected to
      Applications, these usually describe prepurchases of cards, registered
      promotion codes, and migrated balance counts.
    </p>
  </section>

  <table v-show="!loading && timeline.length">
    <tr>
      <th style="min-width: 200px">Time</th>
      <th>Type</th>
      <th>Id</th>
      <th>more</th>
    </tr>
    <tr v-for="item in timeline" :key="item.key" :class="[item.type]">
      <td>{{ item.date.toLocaleString("sv-SE") }}</td>
      <td>{{ item.title }}</td>
      <td>{{ item.id }}</td>

      <td
        :colspan="property == 'message' ? 5 : 1"
        v-for="(property, ix) in Object.keys(item.data)"
        :key="ix"
      >
        {{ property }}: {{ item.data[property] }}
      </td>
      <td>{{ item.balance }}</td>
    </tr>
  </table>
</template>
<style lang="scss" scoped>
td,
td {
  border: 1px silver solid;
}

tr.device > td:nth-child(2) {
  background: lightgreen;
}
tr.eventlog > td:nth-child(2) {
  background: pink;
}
tr.sendlog > td:nth-child(2) {
  background: gold;
}
</style>
